import { useSetLocale, useTranslate } from "@refinedev/core";
import { useForm } from "@refinedev/antd";
import { Col, Form, Input, Row, Select } from "antd";
import { UserIcon } from "@phosphor-icons/react";
import { Avatar, Button } from "@libertify/ui";
import { LanguageSelect } from "components/LanguageSelect";
import { getCountryOptions } from "pages/media/utils";
import { getLanguageCode, getLanguageVariant } from "helpers/core";
import { useAntTheme } from "hooks/useAntTheme";
import { USER_LANGUAGES } from "@/constants";
import { ProfileSectionCard } from "./ProfileSectionCard";

type ProfileDetailsCardProps = Readonly<{
  id: string;
}>;

export const ProfileDetailsCard = ({ id }: ProfileDetailsCardProps) => {
  const t = useTranslate();
  const setLocale = useSetLocale();
  const { theme } = useAntTheme();
  const { formProps, saveButtonProps } = useForm({
    resource: "api/users",
    id: "me",
    action: "edit",
    successNotification: {
      type: "success",
      message: t("pages.users.edit.profileUpdated"),
      description: t("pages.users.edit.yourProfileHas"),
    },
    errorNotification: {
      type: "error",
      message: t("pages.users.edit.updateFailed"),
      description: t("pages.users.edit.failedToUpdate"),
    },
    onMutationSuccess(_data, variables: { language: string }) {
      // language is optional on the form, so a user who saves without ever
      // setting one sends no value here.
      if (variables.language) {
        setLocale(getLanguageCode(variables.language));
      }
    },
  });
  const countriesOptions = getCountryOptions();
  // Watched so the avatar initials follow the name fields as they're edited.
  const firstName = Form.useWatch<string | undefined>(
    "first_name",
    formProps.form
  );
  const lastName = Form.useWatch<string | undefined>(
    "last_name",
    formProps.form
  );
  const fullName = [firstName, lastName].filter(Boolean).join(" ");

  return (
    <ProfileSectionCard
      id={id}
      title={t("pages.user.edit.profileDetails")}
      subtitle={t("pages.user.edit.profileDetailsSubtitle")}
      icon={
        <Avatar
          size="lg"
          name={fullName || undefined}
          icon={fullName ? undefined : <UserIcon />}
          backgroundColor={theme.colorPrimary}
        />
      }
    >
      <Form
        {...formProps}
        initialValues={{
          ...formProps.initialValues,
          // The backend stores the short language code, the selector expects
          // the full locale.
          language: formProps.initialValues?.language
            ? getLanguageVariant(formProps.initialValues.language)
            : undefined,
        }}
        layout="vertical"
        onFinish={(values) => {
          formProps.onFinish?.({
            ...values,
            language: values.language
              ? getLanguageCode(values.language)
              : values.language,
          });
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="first_name"
              label={t("pages.user.edit.firstName")}
              rules={[
                {
                  required: false,
                  message: t("pages.user.edit.pleaseInputYourFirstName"),
                },
              ]}
            >
              <Input
                size="large"
                placeholder={t("pages.user.edit.firstName")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="last_name"
              label={t("pages.user.edit.lastName")}
              rules={[
                {
                  required: false,
                  message: t("pages.user.edit.pleaseInputYourLastName"),
                },
              ]}
            >
              <Input size="large" placeholder={t("pages.user.edit.lastName")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="language"
              label={t("pages.user.edit.language")}
              rules={[
                {
                  required: false,
                  message: t("pages.user.edit.pleaseSelectYourLanguage"),
                },
              ]}
            >
              <LanguageSelect
                size="large"
                placeholder={t("pages.user.edit.selectLanguage")}
                languages={USER_LANGUAGES}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="country"
              label={t("pages.user.edit.country")}
              rules={[
                {
                  required: false,
                  message: t("pages.user.edit.pleaseSelectYourCountry"),
                },
              ]}
            >
              <Select
                size="large"
                showSearch
                placeholder={t("pages.user.edit.selectCountry")}
                options={countriesOptions}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            {...saveButtonProps}
            variant="primary"
            size="l"
            loading={saveButtonProps.loading}
          >
            {t("pages.user.edit.saveChanges")}
          </Button>
        </Form.Item>
      </Form>
    </ProfileSectionCard>
  );
};
